import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSiteContent } from "../context/SiteContentContext";
import { PostComposer } from "../components/PostComposer";
import { PostCard } from "../components/PostCard";
import { TopSpotters } from "../components/TrendingSpots";
import { InviteButton } from "../components/InviteFriends";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Flame, Clock, History, X, Sparkles, Megaphone } from "lucide-react";

const PAGE = 10;

function hashStr(s) {
  let h = 0; for (let i = 0; i < s.length; i++) { h = (h << 5) - h + s.charCodeAt(i); h |= 0; }
  return String(h);
}

function PremiumPopup() {
  const { content } = useSiteContent();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!content.premium_popup_enabled || !content.premium_popup_body) return;
    const key = hashStr((content.premium_popup_title || "") + (content.premium_popup_body || ""));
    if (localStorage.getItem("spotter_premium_dismissed") !== key) {
      const t = setTimeout(() => setOpen(true), 400);
      return () => clearTimeout(t);
    }
  }, [content.premium_popup_enabled, content.premium_popup_title, content.premium_popup_body]);

  const dismiss = () => {
    const key = hashStr((content.premium_popup_title || "") + (content.premium_popup_body || ""));
    localStorage.setItem("spotter_premium_dismissed", key);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="border-4 border-black bg-spotter-cream max-w-lg max-h-[90dvh] overflow-y-auto overscroll-contain top-[5dvh] translate-y-0 p-0" data-testid="premium-popup">
        <div className="sticky top-0 z-10 bg-spotter-maroon text-spotter-cream px-5 py-4 border-b-4 border-black flex items-start gap-2">
          <Sparkles className="w-6 h-6 shrink-0 text-spotter-gold mt-0.5" />
          <DialogTitle className="font-display text-xl font-bold leading-tight text-spotter-cream" data-testid="premium-popup-title">{content.premium_popup_title}</DialogTitle>
        </div>
        <div className="px-5 py-4">
          <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-spotter-maroon font-heading" data-testid="premium-popup-body">{content.premium_popup_body}</p>
        </div>
        <div className="sticky bottom-0 bg-spotter-cream border-t-4 border-black px-5 py-3 flex flex-wrap justify-end gap-2">
          <Button data-testid="premium-popup-dismiss" onClick={dismiss} variant="outline" className="border-2 border-black gap-2">No thanks, I'll keep my free profile</Button>
          <Button data-testid="premium-popup-cta" onClick={() => { dismiss(); navigate("/subscribe"); }} className="bg-spotter-maroon text-spotter-cream border-2 border-black gap-2"><Sparkles className="w-4 h-4" />See Memberships</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const SORTS = [
  { key: "relevant", label: "Relevant", icon: Flame },
  { key: "newest", label: "Newest", icon: Clock },
  { key: "oldest", label: "Oldest", icon: History },
];

export default function Feed() {
  const { content } = useSiteContent();
  const { user } = useAuth();
  const isStaff = ["owner", "admin", "moderator"].includes(user?.role);
  const [posts, setPosts] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sort, setSort] = useState("newest");
  const [date, setDate] = useState("");

  const qs = (skip) => `/posts?skip=${skip}&limit=${PAGE}&sort=${sort}${date ? `&date=${date}&tz=${new Date().getTimezoneOffset()}` : ""}`;

  const load = async () => {
    const { data } = await api.get(qs(0));
    setPosts(data);
    setHasMore(data.length === PAGE);
  };
  useEffect(() => { load(); }, [sort, date]);
  useEffect(() => { api.get("/posts/alerts").then(({ data }) => setAlerts(data)).catch(() => setAlerts([])); }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data } = await api.get(qs(posts.length));
      setPosts((prev) => [...prev, ...data]);
      setHasMore(data.length === PAGE);
    } finally { setLoadingMore(false); }
  };

  const handleDeleted = useCallback((id) => { setPosts((prev) => prev.filter((x) => x.id !== id)); setAlerts((prev) => (prev || []).filter((x) => x.id !== id)); }, []);

  return (
    <div className="max-w-6xl mx-auto">
      <PremiumPopup />
      {content.donation_banner_enabled && content.donation_banner_text && (
        <div className="bg-spotter-cream border-2 border-black rounded-xl p-4 mb-4 brand-shadow-sm" data-testid="donation-banner">
          <p className="text-sm text-spotter-maroon leading-relaxed">{content.donation_banner_text}</p>
          {(content.donation_paypal_url || content.donation_venmo_url) && (
            <div className="flex flex-wrap justify-center gap-2 mt-3">
              {content.donation_paypal_url && <a data-testid="donate-paypal" href={content.donation_paypal_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-white text-sm font-bold rounded-full px-4 py-2 border-2 border-black hover:opacity-90" style={{ background: "#520202" }}>Donate via PayPal</a>}
              {content.donation_venmo_url && <a data-testid="donate-venmo" href={content.donation_venmo_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-white text-sm font-bold rounded-full px-4 py-2 border-2 border-black hover:opacity-90" style={{ background: "#E19C00" }}>Donate via Venmo</a>}
            </div>
          )}
        </div>
      )}

      <div className="lg:grid lg:grid-cols-2 lg:gap-6 lg:items-start">
        {/* LEFT: Prowl Alerts */}
        <aside className="lg:col-span-1 mb-8 lg:mb-0" data-testid="prowl-alerts-column">
          <h2 className="font-display text-2xl font-bold text-spotter-maroon mb-3 flex items-center gap-2"><Megaphone className="w-6 h-6" style={{ color: "#E19C00" }} />Prowl Alerts</h2>
          {isStaff && <PostComposer alert onCreated={(p) => setAlerts((prev) => [p, ...(prev || [])])} />}
          {alerts === null ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-spotter-maroon" /></div>
          ) : alerts.length === 0 ? (
            <div className="bg-white/80 border-2 border-black rounded-xl p-4 text-sm text-spotter-maroon text-center" data-testid="no-alerts">
              {isStaff ? "No alerts yet — post one above to broadcast it to everyone." : "No alerts right now. Check back for important announcements."}
            </div>
          ) : (
            alerts.map((p) => <PostCard key={p.id} post={p} onDeleted={handleDeleted} />)
          )}
        </aside>

        {/* RIGHT: The Prowl */}
        <div className="lg:col-span-1 min-w-0">
          <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
            <h2 className="font-display text-3xl font-bold text-spotter-maroon">The Prowl</h2>
            <InviteButton label="Invite Friends" testid="feed-invite-btn" className="h-9" />
          </div>
          <TopSpotters />
          <PostComposer onCreated={(p) => setPosts((prev) => [p, ...(prev || [])])} />

          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="feed-controls">
            {SORTS.map((s) => {
              const Icon = s.icon;
              return (
                <button key={s.key} data-testid={`sort-${s.key}`} onClick={() => setSort(s.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full border-2 border-black text-xs font-heading font-bold ${sort === s.key ? "bg-spotter-maroon text-spotter-cream" : "bg-white hover:bg-spotter-cream"}`}>
                  <Icon className="w-3.5 h-3.5" />{s.label}
                </button>
              );
            })}
            <div className="flex items-center gap-1 ml-auto">
              <Input data-testid="feed-date-filter" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="border-2 border-black h-8 w-40" />
              {date && <button data-testid="clear-date" onClick={() => setDate("")} className="p-1 border-2 border-black rounded-md bg-white"><X className="w-4 h-4" /></button>}
            </div>
          </div>

          {posts === null ? (
            <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-spotter-maroon" /></div>
          ) : posts.length === 0 ? (
            <div className="text-center py-10 text-spotter-maroon">No posts {date ? "on this date" : "yet"}. Add some Pride or make the first post!</div>
          ) : (
            <>
              {posts.map((p) => (
                <div key={p.id} style={{ contentVisibility: "auto", containIntrinsicSize: "0 600px" }}>
                  <PostCard post={p} onDeleted={handleDeleted} />
                </div>
              ))}
              {hasMore && (
                <div className="flex justify-center pb-6">
                  <Button data-testid="load-more-btn" onClick={loadMore} disabled={loadingMore} variant="outline" className="border-2 border-black bg-white">
                    {loadingMore ? <Loader2 className="w-4 h-4 animate-spin" /> : "Load more"}
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
